import { SEO } from "@/components/SEO";
import { PageSchema } from "@/components/seo/PageSchema";
import { LastUpdated } from "@/components/seo/LastUpdated";
import { TopCTA } from "@/components/cta/TopCTA";
import { MidPageCTA } from "@/components/cta/MidPageCTA";
import { BottomCTA } from "@/components/cta/BottomCTA";
import { ComparisonTable } from "@/components/content/ComparisonTable";
import { VerdictBox } from "@/components/content/VerdictBox";
import { FAQSection } from "@/components/content/FAQSection";
import { InternalLinks } from "@/components/content/InternalLinks";
import { LeadFormSection } from "@/components/LeadFormSection";
import { NavLink } from "@/components/NavLink";

const AgencyVsFreelancer = () => {
  const comparisonData = [
    { feature: "Cost Range", optionA: "$5,000-$25,000+", optionB: "$1,000-$5,000" },
    { feature: "Project Timeline", optionA: "6-12 weeks", optionB: "2-6 weeks" },
    { feature: "Team Resources", optionA: "Full team (designer, dev, SEO)", optionB: "Solo practitioner" },
    { feature: "Ongoing Support", optionA: "Structured retainers", optionB: "Availability varies" },
    { feature: "Industry Expertise", optionA: "Often generalist", optionB: "Can be niche-focused" },
    { feature: "Communication", optionA: "Account managers", optionB: "Direct with creator" },
    { feature: "Revisions", optionA: "Process-driven", optionB: "Flexible" },
    { feature: "Reliability", optionA: "Business continuity", optionB: "Single point of failure" },
  ];

  const verdicts = [
    { label: "Best Value", recommendation: "Niche-focused agency or freelancer" },
    { label: "Lowest Cost", recommendation: "Experienced freelancer" },
    { label: "Most Reliable", recommendation: "Established agency" },
    { label: "Best for Lawn Care", recommendation: "Industry-specialized service" },
    { label: "Ongoing Support", recommendation: "Subscription service" },
  ];

  const faqs = [
    {
      question: "Should I hire an agency or freelancer for my lawn care website?",
      answer: "It depends on your budget and needs. Agencies offer more resources and reliability but cost 3-5x more. Freelancers are budget-friendly but can be risky. Industry-specialized services often deliver the best value."
    },
    {
      question: "How much do agencies charge for lawn care websites?",
      answer: "General agencies charge $5,000-$25,000+ for lawn care websites. Industry-specific agencies may charge $3,000-$10,000. Subscription services offer comparable quality for $150-$500/month."
    },
    {
      question: "Are freelancers reliable for business websites?",
      answer: "Some freelancers are excellent; others disappear mid-project. Check portfolios, reviews, and references carefully. The risk is higher but so are potential savings."
    },
    {
      question: "What if my freelancer stops responding?",
      answer: "This is the main freelancer risk. With an agency or subscription service, you have business continuity. With freelancers, get source files and documentation throughout the project."
    },
    {
      question: "Do agencies understand lawn care businesses?",
      answer: "Most general agencies don't. They'll build a generic site that looks nice but misses industry-specific conversion elements. Specialized services understand your customers and competition."
    },
    {
      question: "How long does it take to build a lawn care website?",
      answer: "Agencies: 6-12 weeks. Freelancers: 2-6 weeks. Specialized subscription services: 2-4 weeks. Faster isn't always better—quality and strategy matter more."
    },
    {
      question: "What about ongoing website maintenance?",
      answer: "Agencies charge $200-$1,000/month for maintenance. Freelancers may not offer it. Subscription services include maintenance in monthly fees, often the best value."
    },
    {
      question: "Should I prioritize cost or quality?",
      answer: "Quality. A cheap website that doesn't generate leads wastes money. A well-built website pays for itself many times over in new lawn care customers."
    },
    {
      question: "What questions should I ask before hiring?",
      answer: "Ask about lawn care industry experience, SEO approach, form conversion strategy, timeline, revision process, and post-launch support. Get references from other service businesses."
    },
    {
      question: "What's the best option for lawn care businesses?",
      answer: "An industry-specialized website subscription service. You get professional quality, lawn care expertise, ongoing optimization, and predictable monthly costs—without agency prices or freelancer risks."
    },
  ];

  const CANONICAL = "https://www.leafrushmarketing.com/comparisons/agency-vs-freelancer/";

  return (
    <>
      <SEO 
        title="Agency vs Freelancer for Lawn Care Websites"
        description="Agency vs freelancer for your lawn care website: Compare costs, timelines, and quality. Find the best option for your landscaping business."
        canonical={CANONICAL}
        ogUrl={CANONICAL}
      />
      <PageSchema 
        title="Agency vs Freelancer for Lawn Care Websites"
        description="Agency vs freelancer for your lawn care website: Compare costs, timelines, and quality."
        url={CANONICAL}
        faqs={faqs}
      />
      
      <article className="py-16">
        <div className="container px-4 md:px-6">
          <LastUpdated />
          
          <header className="max-w-4xl mx-auto text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Agency vs Freelancer for Lawn Care Websites
            </h1>
            <p className="text-xl text-muted-foreground mb-8">
              Big agency, solo freelancer, or something in between? Here's how to choose the right partner for your lawn care website.
            </p>
          </header>

          <TopCTA />

          <div className="max-w-4xl mx-auto">
            <VerdictBox verdicts={verdicts} />

            <section className="mb-12">
              <h2 className="text-2xl font-bold mb-6">Agency vs Freelancer Comparison</h2>
              <p className="text-muted-foreground mb-6">
                When investing in <NavLink to="/lawn-care-website-design/" className="text-primary hover:underline">lawn care website design</NavLink>, 
                choosing the right partner affects your results, timeline, and budget.
              </p>
              <ComparisonTable 
                optionAName="Agency"
                optionBName="Freelancer"
                rows={comparisonData}
              />
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold mb-6">When to Choose an Agency</h2>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <span className="text-primary font-bold">✓</span>
                  <span>Budget of $10,000+ for comprehensive web project</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-primary font-bold">✓</span>
                  <span>Need multiple services (web, SEO, ads, branding)</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-primary font-bold">✓</span>
                  <span>Prefer structured processes and account management</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-primary font-bold">✓</span>
                  <span>Business continuity is critical</span>
                </li>
              </ul>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold mb-6">When to Choose a Freelancer</h2>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <span className="text-primary font-bold">✓</span>
                  <span>Budget under $5,000</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-primary font-bold">✓</span>
                  <span>Found someone with specific lawn care industry experience</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-primary font-bold">✓</span>
                  <span>Comfortable with direct communication and flexibility</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-primary font-bold">✓</span>
                  <span>Have technical backup if freelancer becomes unavailable</span>
                </li>
              </ul>
            </section>

            <MidPageCTA />

            <section className="mb-12">
              <h2 className="text-2xl font-bold mb-6">The Third Option: Specialized Website Subscription</h2>
              <p className="text-muted-foreground mb-4">
                Our <NavLink to="/websites/" className="text-primary hover:underline">lawn care website subscription</NavLink> combines 
                agency quality with freelancer responsiveness—plus industry expertise neither typically offers.
              </p>
              <div className="bg-primary/10 p-6 rounded-lg mb-6">
                <h3 className="font-bold mb-3">Why Subscriptions Win:</h3>
                <ul className="space-y-2">
                  <li>• Predictable monthly cost (no surprise invoices)</li>
                  <li>• Lawn care + landscaping industry expertise</li>
                  <li>• <NavLink to="/service-pages-that-rank/" className="text-primary hover:underline">SEO-optimized service pages</NavLink> included</li>
                  <li>• Ongoing updates and optimization</li>
                  <li>• <NavLink to="/lawn-care-quote-form/" className="text-primary hover:underline">Conversion-focused forms</NavLink> built in</li>
                </ul>
              </div>
              <p className="text-muted-foreground">
                Add <NavLink to="/growth-add-ons/gbp/" className="text-primary hover:underline">GBP optimization</NavLink> and 
                <NavLink to="/growth-add-ons/google-ads/" className="text-primary hover:underline"> Google Ads management</NavLink> for 
                complete lead generation.
              </p>
            </section>

            <FAQSection faqs={faqs} />

            <InternalLinks 
              links={[
                { href: "/comparisons/one-time-website-vs-subscription-lawn-care/", text: "One-Time vs Subscription Websites" },
                { href: "/comparisons/cheap-vs-professional-lawn-care-website/", text: "Cheap vs Professional Websites" },
                { href: "/lawn-care-website-cost/", text: "Website Cost Guide" },
                { href: "/landscaping-website-design/", text: "Landscaping Website Design" },
              ]}
            />
          </div>
        </div>
      </article>

      <BottomCTA />
      
      <LeadFormSection title="Schedule Your Free Consultation" />
    </>
  );
};

export default AgencyVsFreelancer;
